import dynamic from 'next/dynamic'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import CaretDownIcon from 'assets/svg/app/caret-down-slim.svg'
import CaretUpIcon from 'assets/svg/app/caret-up-slim.svg'
import BaseModal from 'components/BaseModal'
import { CardHeader } from 'components/Card'
import { FlexDivRowCentered } from 'components/layout/flex'
import SegmentedControl from 'components/SegmentedControl'
import Spacer from 'components/Spacer'
import { selectShowModalWithParams } from 'state/app/selectors'
import { useAppSelector } from 'state/hooks'

import DepositCrossMargin from '../DepositWithdraw/DepositCrossMargin'
import WithdrawCrossMargin from '../DepositWithdraw/WithdrawCrossMargin'

type Props = {
	onDismiss(): void
	defaultTab: 'deposit' | 'withdraw'
}

const SocketBridge = dynamic(() => import('../../../components/SocketBridge'), {
	ssr: false,
})

const SOCKET_ENABLED = false

const DepositWithdrawCrossMarginModal: React.FC<Props> = ({ onDismiss, defaultTab }) => {
	const { t } = useTranslation()

	const params = useAppSelector(selectShowModalWithParams)?.params

	const [openSocket, setOpenSocket] = useState(false)
	const [transferType, setTransferType] = useState(defaultTab === 'deposit' ? 0 : 1)

	useEffect(() => {
		if (params?.transferType === 'deposit') {
			setTransferType(0)
		} else if (params?.transferType === 'withdraw') {
			setTransferType(1)
		}
	}, [params?.transferType])

	const onChangeTab = (selection: number) => {
		setTransferType(selection)
	}

	const onChangeShowSocket = useCallback(() => setOpenSocket(!openSocket), [openSocket])

	return (
		<StyledBaseModal
			title={
				transferType === 0
					? t('futures.market.trade.margin.modal.deposit.title')
					: t('futures.market.trade.margin.modal.withdraw.title')
			}
			isOpen
			onDismiss={onDismiss}
		>
			<StyledSegmentedControl
				values={['Deposit', 'Withdraw']}
				selectedIndex={transferType}
				onChange={onChangeTab}
			/>
			{SOCKET_ENABLED && transferType === 0 && (
				<>
					<StyledCardHeader onClick={onChangeShowSocket} noBorder={openSocket}>
						<BalanceText>{t('futures.market.trade.margin.modal.bridge.title')}</BalanceText>
						{openSocket ? <CaretUpIcon /> : <CaretDownIcon />}
					</StyledCardHeader>
					{openSocket ? <SocketBridge /> : <Spacer height={20} />}
				</>
			)}
			{transferType === 0 ? <DepositCrossMargin closeOnComplete /> : <WithdrawCrossMargin />}
		</StyledBaseModal>
	)
}

export const StyledBaseModal = styled(BaseModal)`
	[data-reach-dialog-content] {
		width: 400px;
		margin-top: 5vh;
	}

	.card-header {
		padding: 10px 20px 0px;
	}
`

export const BalanceContainer = styled(FlexDivRowCentered)`
	margin-bottom: 8px;
	p {
		margin: 0;
	}
`

export const BalanceText = styled.p<{ $gold?: boolean }>`
	color: ${(props) =>
		props.$gold ? props.theme.colors.selectedTheme.yellow : props.theme.colors.selectedTheme.gray};
	span {
		color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	}
`

export const MaxButton = styled.button`
	height: 22px;
	padding: 4px 10px;
	background: ${(props) => props.theme.colors.selectedTheme.button.background};
	border-radius: 11px;
	font-family: ${(props) => props.theme.fonts.mono};
	font-size: 13px;
	line-height: 13px;
	border: ${(props) => props.theme.colors.selectedTheme.border};
	color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	cursor: pointer;
`

const StyledSegmentedControl = styled(SegmentedControl)`
	margin: 16px 0;
`

const StyledCardHeader = styled(CardHeader)<{ noBorder: boolean }>`
	display: flex;
	justify-content: space-between;
	height: 30px;
	font-size: 13px;
	font-family: ${(props) => props.theme.fonts.regular};
	border-bottom: ${(props) => (props.noBorder ? 'none' : props.theme.colors.selectedTheme.border)};
	margin-left: 0px;
	padding: 0px;
	cursor: pointer;
`

export default DepositWithdrawCrossMarginModal
